import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';

/*
  PRODUCT CONVERSIONS. Ordering stage 3, 11 October 2026.

  **What one is**, from his workbook: an item MedEx has stopped using, and what
  to buy instead. Kept by ClaimSoft number, because the transfer orders and the
  Stock Status are in ClaimSoft numbers and most old numbers are on no catalog
  item. **His answer of 11 October**: they live in the Item Catalog screen.

  **A family is everything connected, in both directions**, his rule of 4
  October: *"if an item has been replaced with say 6 different items we have to
  take into account all of them."* ClaimSoft puts the NEWEST number on a
  transfer order, so following "replaced by" forwards from it finds nothing,
  and the stock under the older numbers would be missed. So the family is built
  first, from every row, and then asked.

  Everything here except the reads and writes is pure, so it can be tested.
*/

export const keyOf = n => String(n ?? '').trim().toUpperCase();
export const NO_REPLACEMENT = 'NO REPLACEMENT';

/* The families, worked out from the rows. */
export function buildFamilies(rows) {
  const parent = new Map();
  const spell = new Map();
  const find = k => { while (parent.get(k) !== k) { parent.set(k, parent.get(parent.get(k))); k = parent.get(k); } return k; };
  const add = (k, as) => { if (!parent.has(k)) parent.set(k, k); if (!spell.has(k)) spell.set(k, String(as).trim()); };
  const join = (a, b) => { const x = find(a), y = find(b); if (x !== y) parent.set(x, y); };

  const byOld = new Map(), byNew = new Map();
  for (const r of rows) {
    const o = keyOf(r.old_number);
    if (!o) continue;
    add(o, r.old_number);
    (byOld.get(o) || byOld.set(o, []).get(o)).push(r);
    const n = keyOf(r.new_number);
    if (n) {
      add(n, r.new_number);
      join(o, n);
      (byNew.get(n) || byNew.set(n, []).get(n)).push(r);
    }
  }
  const members = new Map();
  for (const k of parent.keys()) {
    const root = find(k);
    (members.get(root) || members.set(root, []).get(root)).push(k);
  }
  const familyOf = new Map();
  for (const [root, ks] of members) { ks.sort(); for (const k of ks) familyOf.set(k, root); }
  return { familyOf, members, byOld, byNew, spell };
}

/* The end of the chain from one number: every number reached by following
   "replaced by" until nothing replaces it. More than one when a number was
   replaced by two things. A loop stops rather than spinning. */
export function finalsOf(f, key) {
  const out = new Set(), seen = new Set();
  const walk = k => {
    if (seen.has(k)) return;
    seen.add(k);
    const next = (f.byOld.get(k) || []).map(r => keyOf(r.new_number)).filter(Boolean);
    if (!next.length) { if (k !== key) out.add(k); return; }
    next.forEach(walk);
  };
  walk(key);
  return [...out];
}

/*
  **What a number on a transfer order line is, to the conversions.** His colours
  in the workbook, 4 October: red is an item that has been replaced, yellow is a
  replacement, and either one means "look at the conversion audit".

    replaced        it has a replacement; buy the end of its chain instead
    no_replacement  do not use, and nothing replaces it
    replacement     it replaced something, so older numbers may hold its stock
    null            not in any conversion
*/
export function tagFor(f, number) {
  const k = keyOf(number);
  if (!f.familyOf.has(k)) return null;
  const family = f.members.get(f.familyOf.get(k)).map(m => f.spell.get(m));
  const own = f.byOld.get(k) || [];
  if (own.length) {
    const finals = finalsOf(f, k).map(m => f.spell.get(m));
    if (own.every(r => !keyOf(r.new_number))) return { kind: 'no_replacement', family };
    return { kind: 'replaced', finals, family };
  }
  const replaces = (f.byNew.get(k) || []).map(r => String(r.old_number).trim());
  return { kind: 'replacement', replaces, family };
}

/* A conversion that would make a loop: the new number already leads back to
   the old one. Refused, because a loop has no end to buy. */
export function wouldLoop(rows, oldNumber, newNumber) {
  const o = keyOf(oldNumber), n = keyOf(newNumber);
  if (!n) return false;
  if (o === n) return true;
  const f = buildFamilies(rows);
  const seen = new Set();
  const stack = [n];
  while (stack.length) {
    const k = stack.pop();
    if (k === o) return true;
    if (seen.has(k)) continue;
    seen.add(k);
    for (const r of f.byOld.get(k) || []) { const m = keyOf(r.new_number); if (m) stack.push(m); }
  }
  return false;
}

const COLS = 'id, old_number, old_key, old_vendor, old_description, new_number, new_key, new_vendor, new_description, notes, came_from, created_at, updated_at';

export async function loadConversions() {
  const { data, error } = await fetchAll(() => supabase.from('product_conversions').select(COLS)
    .order('old_key').order('id'));
  return { rows: data || [], error };
}

const tidy = v => { const s = String(v ?? '').trim(); return s === '' ? null : s; };
const isNone = v => !tidy(v) || keyOf(v) === NO_REPLACEMENT;

function cleanRow(c) {
  const none = isNone(c.new_number);
  return {
    old_number: tidy(c.old_number), old_vendor: tidy(c.old_vendor), old_description: tidy(c.old_description),
    new_number: none ? null : tidy(c.new_number),
    new_vendor: none ? null : tidy(c.new_vendor), new_description: none ? null : tidy(c.new_description),
    notes: tidy(c.notes),
  };
}

function refuse(message) { return { ok: false, error: { message } }; }

/*
  **Add one, or change one.** Refused with nothing saved if the old number is
  empty, if it is its own replacement, if the same pair is already there, or if
  it would make a loop. A save is proved by the row coming back.
*/
export async function saveConversion({ id, userId, ...fields }, existing) {
  const row = cleanRow(fields);
  if (!row.old_number) return refuse('Type the old item number.');
  if (row.new_number && keyOf(row.new_number) === keyOf(row.old_number)) return refuse('An item cannot replace itself.');
  const others = existing.filter(r => r.id !== id);
  const same = others.find(r => r.old_key === keyOf(row.old_number) && (r.new_key || '') === keyOf(row.new_number));
  if (same) return refuse(row.new_number
    ? row.old_number + ' is already replaced by ' + row.new_number + '.'
    : row.old_number + ' is already marked as having no replacement.');
  if (wouldLoop(others, row.old_number, row.new_number)) {
    return refuse(row.new_number + ' already leads back to ' + row.old_number + ', so this would go round in a circle.');
  }
  const now = new Date().toISOString();
  const q = id
    ? supabase.from('product_conversions').update({ ...row, updated_by: userId || null, updated_at: now }).eq('id', id)
    : supabase.from('product_conversions').insert({ ...row, came_from: 'by_hand', created_by: userId || null, updated_by: userId || null });
  const { data, error } = await q.select(COLS).maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('That did not save.');
  return { ok: true, row: data };
}

export async function removeConversion(id) {
  const { data, error } = await supabase.from('product_conversions').delete().eq('id', id).select('id').maybeSingle();
  if (error) return { ok: false, error };
  if (!data) return refuse('That was not removed. It may already have gone.');
  return { ok: true };
}

/*
  **Reading his conversions sheet.** The columns of his workbook's Product
  Conversions sheet: DNU Item, Vendor, Description, Replacement Item, Vendor,
  Description, and then two worked-out columns, Final Replacement and Final
  Family, which OnHand works out for itself and so ignores. The two Vendor
  columns share a name, so they are read by position after the item columns.
*/
export function parseConversionSheet(table) {
  const at = table.findIndex(r => r.some(c => /^dnu item$/i.test(String(c).trim())));
  if (at < 0) return { rows: [], problem: 'This does not look like a conversions list: it has no "DNU Item" column.' };
  const head = table[at].map(c => String(c).trim().toLowerCase());
  const o = head.indexOf('dnu item'), n = head.indexOf('replacement item');
  if (n < 0) return { rows: [], problem: 'This conversions list has no "Replacement Item" column.' };
  const rows = [];
  for (const r of table.slice(at + 1)) {
    if (!tidy(r[o])) continue;
    rows.push(cleanRow({ old_number: r[o], old_vendor: r[o + 1], old_description: r[o + 2],
      new_number: r[n], new_vendor: r[n + 1], new_description: r[n + 2] }));
  }
  return { rows, problem: rows.length ? null : 'There are no conversions in this file.' };
}

/*
  **What bringing a list in would do**, worked out before anything is saved so
  he sees it first. Per old number, the replacements in the file are compared
  with the ones in OnHand: the same is left alone, new is added, different is
  changed to what the file says. **An old number in OnHand that the file does not
  mention is left alone**, never removed, because a list that is missing a row
  is not him deciding to remove it.
*/
export function planImport(fileRows, existing) {
  const have = new Map();
  for (const r of existing) (have.get(r.old_key) || have.set(r.old_key, []).get(r.old_key)).push(r);
  const want = new Map();
  for (const r of fileRows) {
    const k = keyOf(r.old_number);
    (want.get(k) || want.set(k, []).get(k)).push(r);
  }
  const plan = { add: [], change: [], same: 0, problems: [] };
  for (const [k, rows] of want) {
    const seen = new Set();
    const uniq = rows.filter(r => { const nk = keyOf(r.new_number); if (seen.has(nk)) return false; seen.add(nk); return true; });
    if (uniq.some(r => keyOf(r.new_number) === k)) { plan.problems.push(rows[0].old_number + ' is listed as replacing itself, so it was left out.'); continue; }
    const had = have.get(k) || [];
    const a = [...new Set(had.map(r => r.new_key || ''))].sort().join('|');
    const b = [...new Set(uniq.map(r => keyOf(r.new_number)))].sort().join('|');
    if (!had.length) plan.add.push(...uniq);
    else if (a === b) plan.same++;
    else plan.change.push({ old_number: uniq[0].old_number, from: had, to: uniq });
  }
  return plan;
}

export async function applyImport(plan, userId, fileName) {
  const came = 'file ' + fileName + ' ' + new Date().toISOString().slice(0, 10);
  const stamp = r => ({ ...r, came_from: came, created_by: userId || null, updated_by: userId || null });
  for (const c of plan.change) {
    const ids = c.from.map(r => r.id);
    const { error } = await supabase.from('product_conversions').delete().in('id', ids);
    if (error) return { ok: false, error };
  }
  const rows = [...plan.add, ...plan.change.flatMap(c => c.to)].map(stamp);
  for (let i = 0; i < rows.length; i += 500) {
    const { data, error } = await supabase.from('product_conversions').insert(rows.slice(i, i + 500)).select('id');
    if (error) return { ok: false, error };
    if (!data || data.length !== Math.min(500, rows.length - i)) return refuse('Only some of the conversions saved. Open the list and check it.');
  }
  return { ok: true, saved: rows.length };
}
